// Token storage and current-user fetching.
//
// Tokens live in localStorage under `dreambook.token` so a full page reload
// keeps the session, and so EVERY tab shares one token. Access-token expiry is
// tracked separately so the SPA can refresh silently before the short-lived
// JWT lapses (PRD-ACC-001.3).
//
// Cross-tab correctness (2026-07 E2E fix): reads go straight to localStorage on
// every call — no in-memory memo. The old module-level cache captured the token
// once per tab; when another tab refreshed (rotating the token), this tab kept
// sending the stale JWT and silently 401'd forever. localStorage reads are
// sub-microsecond, so the cache bought nothing worth that failure mode.

const KEY = "dreambook.token";
const EXPIRY_KEY = "dreambook.token_expires_at";

/** localStorage key for the access token — exported so AuthContext can watch the
 *  cross-tab `storage` event for it and adopt rotations/sign-outs immediately. */
export const TOKEN_STORAGE_KEY = KEY;

export interface TokenResponse {
  access_token: string;
  token_type?: string;
  expires_at?: string;
  expires_in?: number;
}

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    window.localStorage.setItem(KEY, token);
  } catch {
    /* ignored — sessionless fallback is OK */
  }
}

/** Persist access token + expiry from a login/refresh response. */
export function applyTokenResponse(tok: TokenResponse): void {
  setToken(tok.access_token);
  let expiresAtMs: number | null = null;
  if (tok.expires_at) {
    const parsed = Date.parse(tok.expires_at);
    if (!Number.isNaN(parsed)) expiresAtMs = parsed;
  } else if (typeof tok.expires_in === "number" && tok.expires_in > 0) {
    expiresAtMs = Date.now() + tok.expires_in * 1000;
  }
  try {
    if (expiresAtMs === null) window.localStorage.removeItem(EXPIRY_KEY);
    else window.localStorage.setItem(EXPIRY_KEY, String(expiresAtMs));
  } catch {
    /* ignored */
  }
}

/** Unix-ms expiry of the current access token, if known. Read live from storage
 *  (same cross-tab reasoning as `getToken`). */
export function getTokenExpiresAt(): number | null {
  try {
    const raw = window.localStorage.getItem(EXPIRY_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(KEY);
    window.localStorage.removeItem(EXPIRY_KEY);
  } catch {
    /* ignored */
  }
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  role: "user" | "admin";
  // Billing state mirrored from /api/auth/me. "free" by default.
  plan: "free" | "author" | "studio";
  subscription_status: string | null;
  // "stripe" | "razorpay" | null. Drives which portal/cancel control the
  // Account page renders.
  billing_provider: "stripe" | "razorpay" | null;
  stripe_customer_id: string | null;
  razorpay_customer_id: string | null;
  razorpay_subscription_id: string | null;
  email_verified: boolean;
  // Whether a Google account is linked, and whether a password is set. The
  // Account page uses both to drive the "Connected accounts" controls (a
  // Google-only account can't disconnect Google without a password).
  google_linked: boolean;
  has_password: boolean;
  created_at: string;
  // PRD-CORE-004 creation-flow experiment bucket. The backend always sends
  // this; the SPA reads it to pick the v1/v2 studio entry point.
  experiments: { creation_flow: "v1" | "v2" };
}
